import React, { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Link, Navigate } from 'react-router-dom';
import { Award, TrendingUp, Gift, Upload, Sparkles, ArrowUpRight } from 'lucide-react';

const TIER_NAMES = ['silver', 'gold', 'platinum', 'black'];

export default function Loyalty() {
    const { user, type, loading } = useAuth();
    const [data, setData] = useState(null);
    const [ledger, setLedger] = useState([]);

    useEffect(() => {
        if (type === 'customer') {
            api.get('/loyalty/me').then(({ data }) => setData(data));
            api.get('/loyalty/me/ledger').then(({ data }) => setLedger(data));
        }
    }, [type]);

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (!loading && type !== 'customer') return <Navigate to="/login?next=/loyalty" replace />;
    if (!data) return <div className="min-h-screen flex items-center justify-center">Loading loyalty…</div>;

    return (
        <div className="pt-16 pb-32" data-testid="loyalty-page">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
                <div className="overline mb-4">Inorbit One Rewards</div>
                <h1 className="font-serif text-5xl md:text-6xl text-navy leading-tight">Welcome back, <span className="italic text-gold">{user.name.split(' ')[0]}.</span></h1>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 mt-12 grid lg:grid-cols-3 gap-8">
                {/* MEMBERSHIP CARD */}
                <div className={`tier-${data.tier} p-8 md:p-10 min-h-[280px] flex flex-col justify-between relative overflow-hidden`} data-testid="membership-card">
                    <div className="relative z-10">
                        <div className="overline" style={{ color: 'rgba(255,255,255,0.7)' }}>Inorbit One</div>
                        <div className="font-serif text-3xl mt-2">{data.tier.charAt(0).toUpperCase() + data.tier.slice(1)} Member</div>
                    </div>
                    <div className="relative z-10 mt-8">
                        <div className="text-[10px] tracking-widest uppercase opacity-70 mb-2">Member ID</div>
                        <div className="font-mono text-base md:text-lg tracking-wider mb-6">{data.membership_id}</div>
                        <div className="flex items-end justify-between gap-4">
                            <div className="min-w-0">
                                <div className="text-[10px] tracking-widest uppercase opacity-70">Points Balance</div>
                                <div className="font-serif text-4xl md:text-5xl mt-1">{data.points_balance.toLocaleString()}</div>
                            </div>
                            <div className="w-16 h-16 bg-white/20 backdrop-blur flex items-center justify-center text-xs tracking-widest shrink-0" style={{ border: '1px solid rgba(255,255,255,0.3)' }}>
                                QR
                            </div>
                        </div>
                    </div>
                </div>

                {/* PROGRESS */}
                <div className="lg:col-span-2 card-luxury p-10" data-testid="tier-progress">
                    <div className="overline mb-4">Your Journey</div>
                    {data.next_tier ? (
                        <>
                            <h3 className="font-serif text-3xl text-navy mb-2">
                                {(data.next_threshold - data.lifetime_points).toLocaleString()} points to <span className="italic text-gold">{data.next_tier}.</span>
                            </h3>
                            <p className="text-text-secondary text-sm mb-8">Earn points across all Inorbit malls to climb the tiers.</p>
                            <div className="h-1 bg-stone relative overflow-hidden">
                                <div className="h-full bg-gold transition-all duration-1000" style={{ width: `${data.progress_percent}%` }} />
                            </div>
                            <div className="mt-3 flex justify-between text-xs text-text-tertiary">
                                <span>{data.lifetime_points.toLocaleString()} lifetime pts</span>
                                <span>{data.next_threshold.toLocaleString()} pts goal</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 className="font-serif text-3xl text-navy mb-3">You're at the pinnacle.</h3>
                            <p className="text-text-secondary text-sm">Enjoy 2x points, lounge access, valet, luxury concierge and private events.</p>
                        </>
                    )}
                    <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
                        {TIER_NAMES.map((t) => (
                            <div key={t} className={`text-center p-4 border ${t === data.tier ? 'border-gold' : 'border-border-subtle'}`}>
                                <div className={`text-xs uppercase tracking-widest font-semibold ${t === data.tier ? 'text-gold' : 'text-text-tertiary'}`}>{t}</div>
                                {t === data.tier && <div className="overline mt-1">Current</div>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TIER BENEFITS */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
                <div className="overline mb-4">Your Benefits</div>
                <h3 className="font-serif text-3xl text-navy mb-8">Privileges of a {data.tier} member</h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {data.tier_benefits.map((b, i) => (
                        <div key={i} className="card-luxury p-6">
                            <div className="text-2xl text-gold font-serif mb-2">0{i + 1}</div>
                            <div className="text-sm text-navy">{b}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* QUICK ACTIONS */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { icon: Upload, t: 'Upload Bill', to: '/bills', d: 'Earn points instantly' },
                    { icon: Gift, t: 'Redeem Rewards', to: '/rewards', d: 'Vouchers & experiences' },
                    { icon: Sparkles, t: 'Daily Games', to: '/games', d: 'Spin, scratch, win' },
                    { icon: Award, t: 'AI Concierge', to: '/concierge', d: 'Personal shopping help' },
                ].map((a) => (
                    <Link to={a.to} key={a.t} className="card-luxury p-6 group flex items-center justify-between" data-testid={`quick-${a.t.toLowerCase().replace(/\s+/g, '-')}`}>
                        <div>
                            <a.icon size={22} className="text-gold mb-3" strokeWidth={1.3} />
                            <div className="font-serif text-xl text-navy">{a.t}</div>
                            <div className="text-xs text-text-tertiary mt-1">{a.d}</div>
                        </div>
                        <ArrowUpRight size={20} strokeWidth={1.3} className="text-text-tertiary group-hover:text-gold" />
                    </Link>
                ))}
            </section>

            {/* LEDGER */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20" data-testid="ledger-section">
                <div className="overline mb-4">Points Ledger</div>
                <h3 className="font-serif text-3xl text-navy mb-8">Your activity</h3>
                <div className="card-luxury divide-y divide-border-subtle">
                    {ledger.length === 0 ? <div className="p-8 text-text-tertiary text-center text-sm">No activity yet — upload a bill or play a game.</div> :
                        ledger.map((l) => (
                            <div key={l.id} className="px-6 py-5 flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-navy">{l.description}</div>
                                    <div className="text-xs text-text-tertiary mt-1">{new Date(l.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} • {l.source.replace('_', ' ')}</div>
                                </div>
                                <div className={`font-mono text-lg ${l.direction === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                    {l.direction === 'credit' ? '+' : '−'}{l.points}
                                </div>
                            </div>
                        ))
                    }
                </div>
            </section>
        </div>
    );
}
